// @ts-strict-ignore
import { JsonObject, JsonProperty } from '@shared/convert/convert-core';
import { DefinedMetaElementPropertyVO, MetaElementUsageVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { PropertyInLocalesVO, PropertyVO, StringValueInLocalesVO } from '@project/components/article/model/vo/property.vo';
import { RepresentationVO } from '@shared/vo/representation.vo';
import { TextureElementVO } from './texture-element.vo';

@JsonObject('ArticleTypeVO')
export class ArticleTypeVO {
  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('displayName', 'String')
  public displayName: string = undefined;
}


@JsonObject('ArticleUsageVO')
export class ArticleUsageVO {
  @JsonProperty('id', 'String')
  public id: string = undefined;

  @JsonProperty('groupId', 'String')
  public groupId: string = undefined;

  @JsonProperty('articleId', 'Number')
  public articleId: number = undefined;

  @JsonProperty('articleVersion', 'Number')
  public articleVersion: number = undefined;

  @JsonProperty('metaElementUsages', 'MetaElementUsageVOsConverter', true)
  public metaElementUsages: MetaElementUsageVO[] = undefined;

  @JsonProperty('definedMetaElementPlausiProperties', '[DefinedMetaElementPropertyVO]')
  public definedMetaElementPlausiProperties: DefinedMetaElementPropertyVO[] = [];

  @JsonProperty('definedMetaElementInfoProperties', '[DefinedMetaElementPropertyVO]')
  public definedMetaElementInfoProperties: DefinedMetaElementPropertyVO[] = [];

  @JsonProperty('plausiProperties', '[PropertyVO]')
  public plausiProperties: PropertyVO[] = [];

  @JsonProperty('infoProperties', '[PropertyVO]')
  public infoProperties: PropertyVO[] = [];

  @JsonProperty('articleVisible', 'Boolean')
  public articleVisible: boolean = true;
}

@JsonObject('ArticleCompositionVO')
export class ArticleCompositionVO {
  /**
    * identificator that is unique within this composition. It is needed in
    * order to distinguish usages of the same article
    */
  @JsonProperty('id', 'String')
  public id: string = undefined;

  /**
   * value objects for representations of this composition
   */
  @JsonProperty('representations', '[RepresentationVO]')
  public representations: RepresentationVO[] = undefined;

  /**
   * value objects for articles of this composition
   */
  @JsonProperty('articleUsages', '[ArticleUsageVO]')
  public articleUsages: ArticleUsageVO[] = undefined;

  /**
   * info properties for composition
   */
  @JsonProperty('infoProperties', '[PropertyVO]')
  public infoProperties: PropertyVO[] = [];
}

@JsonObject('ArticleVO')
export class ArticleVO {

  public static readonly CLASS_NAME: string = 'ArticleVO';

  @JsonProperty('classType', 'string')
  public classType: string = undefined;

  @JsonProperty('identity', 'Number')
  public identity: number = undefined;

  @JsonProperty('version', 'Number')
  public version: number = undefined;

  @JsonProperty('displayName', 'StringValueInLocalesVOConverter', true)
  public displayName: StringValueInLocalesVO = undefined;

  @JsonProperty('articleNo', 'String')
  public articleNo: string = undefined;

  @JsonProperty('eanNo', 'Number')
  public eanNo: number = undefined;

  @JsonProperty('publicGlobal', 'boolean')
  public publicGlobal: boolean = undefined;

  @JsonProperty('lastCall', 'DateConverter', true)
  public lastCall: Date = undefined;

  @JsonProperty('materialStatus', 'Number')
  public materialStatus: number = undefined;

  @JsonProperty('type', 'ArticleTypeVO')
  public type: ArticleTypeVO = undefined;

  @JsonProperty('representations', 'RepresentationVOsConverter', true)
  public representations: RepresentationVO[] = undefined;

  @JsonProperty('metaElements', 'MetaElementVOsConverter', true)
  public metaElements: MetaElementVO[] = undefined;

  @JsonProperty('textureElements', '[TextureElementVO]')
  public textureElements: TextureElementVO[] = [];

  @JsonProperty('connectionRequirements', '[PropertyVO]')
  public connectionRequirements: PropertyVO[] = undefined;

  @JsonProperty('connectionFeatures', '[PropertyVO]')
  public connectionFeatures: PropertyVO[] = undefined;

  @JsonProperty('preloadedTechnicalInfos', '[PropertyInLocalesVO]')
  public preloadedTechnicalInfos: PropertyInLocalesVO[] = undefined;

  @JsonProperty('behaviorProperties', '[PropertyVO]')
  public behaviorProperties: PropertyVO[] = undefined;

  @JsonProperty('plausiProperties', '[PropertyVO]')
  public plausiProperties: PropertyVO[] = undefined;

  @JsonProperty('composition', 'ArticleCompositionVO')
  public composition: ArticleCompositionVO = undefined;

  @JsonProperty('baseArticle', 'ArticleVOConverter', true)
  public baseArticle: ArticleVO = undefined;

  @JsonProperty('articleNote', 'String')
  public articleNote: string = undefined;

  @JsonProperty('modelId', 'Number')
  public modelId: number = undefined;

  @JsonProperty('isImportedFile', 'Boolean')
  public isImportedFile: boolean;

  public constructor() {
    this.classType = ArticleVO.CLASS_NAME;
  }

  public get series() {
    return this.articleNo.split('-')[0].replace(/^0*/g, '');
  }
}

@JsonObject('ArticleCollectionVO')
export class ArticleCollectionVO {
  public static readonly TYPE_NORMAL: string = 'NORMAL';
  public static readonly TYPE_FAVORITES: string = 'FAVORITES';

  public static readonly SORT_BY_ARTICLE_NO: string = 'BY_ARTICLE_NO';
  public static readonly SORT_BY_OWN_ARTICLE_NO: string = 'BY_OWN_ARTICLE_NO';

  @JsonProperty('id', 'Number')
  public id: number = undefined;

  @JsonProperty('type', 'String')
  public type: string = ArticleCollectionVO.TYPE_NORMAL;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('userId', 'Number')
  public userId: number = undefined;

  @JsonProperty('articleIds', '[Number]')
  public articleIds: number[] = [];

  @JsonProperty('sorting', 'String')
  public sorting: string = ArticleCollectionVO.SORT_BY_ARTICLE_NO;

  public static create(name: string = null, type: string = this.TYPE_NORMAL, articleIds: number[] = null): ArticleCollectionVO {
    const articleCollectionVO: ArticleCollectionVO = new ArticleCollectionVO();
    articleCollectionVO.name = name;
    articleCollectionVO.type = type;
    articleCollectionVO.articleIds = !articleIds ? [] : articleIds;
    return articleCollectionVO;
  }
}

@JsonObject('ArticleTechnicalInfoVO')
export class ArticleTechnicalInfoVO {
  @JsonProperty('articleTechnicalInfoVOs', '[PropertyInLocalesVO]')
  public articleTechnicalInfoVOs: PropertyInLocalesVO[] = undefined;
}

export interface ArticleTechnicalInfoBundle {
  articleVO: ArticleVO;
  articleTechnicalInfoVO: ArticleTechnicalInfoVO;
}

@JsonObject('ArticleTypeClassVO')
export class ArticleTypeClassVO {
  @JsonProperty('classId', 'string')
  public classId: string = undefined;

  @JsonProperty('locale', 'string')
  public locale: string = undefined;

  @JsonProperty('description', 'string')
  public description: string = undefined;

  @JsonProperty('sortOrder', 'Number')
  public sortOrder: number = undefined;
}

@JsonObject('ArticleIdMappingVO')
export class ArticleIdMappingVO {
  @JsonProperty('oldArticleId', 'Number')
  public oldArticleId: number = undefined;

  @JsonProperty('articleNo', 'String')
  public articleNo: string = undefined;

  @JsonProperty('eanNo', 'Number')
  public eanNo: number = undefined;

  @JsonProperty('materialStatus', 'Number')
  public materialStatus: number = undefined;

  @JsonProperty('lastCall', 'DateConverter', true)
  public lastCall: Date = undefined;

  @JsonProperty('publicGlobal', 'Boolean')
  public publicGlobal: boolean = undefined;

  @JsonProperty('minimumStockLevel', 'Number')
  public minimumStockLevel: number = undefined;

  @JsonProperty('isFullArticle', 'Boolean')
  public isFullArticle: boolean = undefined;
}

@JsonObject('ArticleMetadataVO')
export class ArticleMetadataVO {
  @JsonProperty('articleId', 'Number')
  public articleId: number | undefined = undefined;

  @JsonProperty('articleIdMapping', 'ArticleIdMappingVO')
  public articleIdMapping: ArticleIdMappingVO;

  @JsonProperty('articleType', 'ArticleTypeVO')
  public articleType: ArticleTypeVO;
}
